import dayjs from "dayjs";
import { TFunction } from "i18next";

import { ChartData } from "../components/ChartCard/ChartCard.types";
import { tooltipTitles } from "../constants/measurements";

export const getBloodPressurePairedValues = (
  data: Map<string, ChartData[]> | Map<string, any[]>
) => {
  const allEntries = Array.from(data.values()).flatMap((source) =>
    Array.from(source.values()).flat()
  );

  const groupedByTime = new Map<
    number,
    { bp_systolic?: number; bp_diastolic?: number }
  >();

  allEntries.forEach((entry) => {
    const time = entry.unformattedTime;
    if (time === undefined) return;

    if (!groupedByTime.has(time)) {
      groupedByTime.set(time, {});
    }

    const current = groupedByTime.get(time)!;

    if ("bp_systolic" in entry) current.bp_systolic = entry.bp_systolic;
    if ("bp_diastolic" in entry) current.bp_diastolic = entry.bp_diastolic;
  });

  const sortedTimes = Array.from(groupedByTime.keys()).sort((a, b) => a - b);

  const filtered = sortedTimes.filter((time) => {
    const val = groupedByTime.get(time);
    return val?.bp_systolic !== undefined && val?.bp_diastolic !== undefined;
  });

  return {
    xValues: filtered,
    yValues: {
      systolic: filtered.map((time) => groupedByTime.get(time)!.bp_systolic!),
      diastolic: filtered.map((time) => groupedByTime.get(time)!.bp_diastolic!),
    },
  };
};

export const getDataUnformattedtimeAndValues = (
  data: Map<string, ChartData[]> | Map<string, any[]>,
  yKey: string
) => {
  const entries = Array.from(data.values())
    .flatMap((source) => Array.from(source.values()).flat())
    .flat()
    .filter((dataPoint) => dataPoint)
    .filter((dataPoint) => !dataPoint.source.includes("archived"))
    .sort((a, b) => a.unformattedTime - b.unformattedTime);

  return {
    xValues: entries.map((entry) => entry.unformattedTime),
    yValues: entries.map((entry) => entry[yKey]),
  };
};

export const getLatestBloodPressureValue = (
  systolicMainValues: any,
  diastolicMainValues: any
) => {
  const systolicDate = systolicMainValues.latestDate
    ? dayjs(systolicMainValues.latestDate)
    : null;
  const diastolicDate = diastolicMainValues.latestDate
    ? dayjs(diastolicMainValues.latestDate)
    : null;

  if (systolicDate && diastolicDate && systolicDate.isSame(diastolicDate)) {
    return `${systolicMainValues.latest}/${diastolicMainValues.latest}`;
  }

  if (systolicDate && (!diastolicDate || systolicDate.isAfter(diastolicDate))) {
    return `${systolicMainValues.latest}/-- mmHg`;
  }

  if (diastolicDate && (!systolicDate || diastolicDate.isAfter(systolicDate))) {
    return `--/${diastolicMainValues.latest} mmHg`;
  }

  return "-";
};

export function getTooltipTitleFor(key: string): string {
  const title = tooltipTitles.get(key);

  if (!title) {
    return key.replace(/_/g, " ").replace(/\b[a-z]/g, (m) => m.toUpperCase());
  }

  return title;
}

/**
 * The label for a FibriCheck arrhythmia state, in the user's language.
 *
 * EN-5191 follow-up: both pill render sites built this by prettifying the raw webhook key
 * ("possibly_irregular" -> "Possibly irregular"), so the tag stayed English in every language
 * — it never passed through a translation at all. This is the same fix EN-5212 made on the
 * mobile side; those rows are seeded as UsedLocation = 'Mobile' and are not served to the
 * portal, hence a portal key per state.
 *
 * The prettified key remains the fallback: FibriCheck can introduce a state before we carry a
 * row for it, and a readable English word beats a raw key in a clinical tag.
 */
export function getArrhythmiaStateLabel(key: string, t: TFunction): string {
  const prettified = key
    .replace(/_/g, " ")
    .replace(/^./, (char) => char.toUpperCase());

  return t(`patientPage_fibricheckState_${key}`, {
    defaultValue: prettified,
  });
}
